import { datadogRum } from '@datadog/browser-rum';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { useFeatureFlag } from '@/src/application/hooks/util/useFeatureFlag';
import { formValidations } from '@/src/constants/FormValidations';
import { atomActionDragDrop } from '@/src/modules/CampaignsModule/atoms/recurrentActionsAtom';
import {
  TRANSLATE_MODAL_DUPLICATE,
  WHATSAPP_PREFIX,
} from '@/src/modules/CampaignsModule/constants';

import { useActionsFilters } from './useActionsFilters';
import { useDeprecatedBanner } from './useDeprecatedBanner';

import {
  atomActionDetailQuickTools,
  atomFiltersAction,
  atomSelectedRowActionName,
  atomSelectedRowActionType,
  atomSelectedRowsAction,
  atomSelectedRowsQuickTools,
} from '@/modules/CampaignsModule/atoms/actionsAtom';
import {
  atomDuplicateActionInputValue,
  atomLoadingDuplicateAction,
} from '@/modules/CampaignsModule/atoms/campaignAtom';
import { atomToggleDuplicateActionsModal } from '@/modules/CampaignsModule/atoms/modalsCampaignAtom';
import { useCampaignActions } from '@/modules/CampaignsModule/hooks/useCampaignActions';
import { useValidNameResource } from '@/modules/CampaignsModule/hooks/useValidNameResource';
import { EChannelsID, EResourceType } from '@/modules/CampaignsModule/interfaces/CampaignActions';

interface useDuplicateActionProps {
  campaignId?: string | number;
  actionId?: string | number;
}

export const useDuplicateActions = ({ campaignId, actionId }: useDuplicateActionProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { campaignId: campaignIdPath, actionId: actionIdPath } = useParams();

  // TODO remove this when the feature flag showDragDropRemovalNotice is removed
  const disableLegacyAction = useFeatureFlag('disableLegacyActionDuplication');

  const { inputLengths, formRegExp } = formValidations;

  const [loading, setLoading] = useAtom(atomLoadingDuplicateAction);
  const [actionDetailQuickTools, setActionDetailQuickTools] = useAtom(atomActionDetailQuickTools);
  const filtersAction = useAtomValue(atomFiltersAction);
  const actionTypeNumber = useAtomValue(atomSelectedRowActionType);
  const rowActionName = useAtomValue(atomSelectedRowActionName);
  const [toggleDuplicateActionsModal, setToggleDuplicateActionsModal] = useAtom(
    atomToggleDuplicateActionsModal
  );
  const actionDragDrop = useAtomValue(atomActionDragDrop);
  const [selectedRowsQuickTools, setSelectedRowsQuickTools] = useAtom(atomSelectedRowsQuickTools);
  const setSelectedRows = useSetAtom(atomSelectedRowsAction);
  const [duplicateActionInputValue, setDuplicateActionInputValue] = useAtom(
    atomDuplicateActionInputValue
  );

  const { fetchServiceValidName } = useValidNameResource();
  const { duplicateAction } = useCampaignActions();
  const { showDeprecatedDragDropForRow } = useDeprecatedBanner();
  const { getActions } = useActionsFilters();

  const actionName = useMemo(
    () => rowActionName || actionDetailQuickTools?.name,
    [actionDetailQuickTools?.name, rowActionName]
  );

  const isWhatsappAction = useMemo(() => {
    return actionName?.startsWith(WHATSAPP_PREFIX) || false;
  }, [actionName]);

  const campaignIdMemo = useMemo(
    () => (campaignId ? Number(campaignId) : Number(campaignIdPath)),
    [campaignId, campaignIdPath]
  );

  const actionIdMemo = useMemo(
    () => (actionId ? Number(actionId) : Number(actionIdPath)),
    [actionId, actionIdPath]
  );

  const actionTypeMemo = useMemo(
    () => (actionTypeNumber ? actionTypeNumber : actionDetailQuickTools?.type),
    [actionTypeNumber, actionDetailQuickTools?.type]
  );

  const disabledSubmitButton = useMemo(
    () => disableLegacyAction && actionDragDrop,
    [disableLegacyAction, actionDragDrop]
  );

  const VALIDATION_RULES = useMemo(
    () => ({
      input: {
        required: {
          value: true,
          message: t('FORMS_RULES_MESSAGES.required'),
        },
        minLength: {
          value: inputLengths.min,
          message: t('FORMS_RULES_MESSAGES.minLength'),
        },
        maxLength: {
          value: isWhatsappAction ? inputLengths.maxWhatsApp : inputLengths.maxAction,
          message: isWhatsappAction
            ? t('FORMS_RULES_MESSAGES.maxLengthWhatsApp')
            : t('FORMS_RULES_MESSAGES.maxLengthAction'),
        },
        pattern: {
          value: formRegExp.lettersNumbersAndSpecials,
          message: t('FORMS_RULES_MESSAGES.lettersNumbersAndSpecials'),
        },
      },
    }),
    [formRegExp.lettersNumbersAndSpecials, inputLengths, t, isWhatsappAction]
  );

  const alertConfig = useMemo(() => {
    let config = {
      description: '',
      type: 'info' as 'info' | 'warning',
    };

    if (disabledSubmitButton) {
      config = {
        description: t('CAMPAIGN_ACTIONS_MAIN.DUPLICATE_ACTIONS.disableActions'),
        type: 'warning',
      };
    } else if (!showDeprecatedDragDropForRow) {
      config = {
        description: t(`${TRANSLATE_MODAL_DUPLICATE}.description`),
        type: 'info',
      };
    } else {
      config = {
        description: t('CAMPAIGN_ACTIONS_MAIN.DUPLICATE_ACTIONS.deprecated.featured'),
        type: 'warning',
      };
    }

    return config;
  }, [disabledSubmitButton, showDeprecatedDragDropForRow, t]);

  const handleCloseModal = useCallback(() => {
    setToggleDuplicateActionsModal(false);
    setTimeout(() => {
      if (selectedRowsQuickTools) {
        setSelectedRowsQuickTools(false);
        setSelectedRows([]);
      }
      setDuplicateActionInputValue('');
    }, 400);
  }, [
    selectedRowsQuickTools,
    setDuplicateActionInputValue,
    setSelectedRows,
    setSelectedRowsQuickTools,
    setToggleDuplicateActionsModal,
  ]);

  const handleValidation = useCallback(
    async (value: string) => {
      const isValidName = await fetchServiceValidName({
        name: value,
        resource: EResourceType.ACTION,
        campaignId: campaignIdMemo,
      });
      return {
        isValidInput: isValidName,
        message: t('CAMPAIGNS_MAIN.CREATE_EDIT_CAMPAIGNS.existCampaign'),
      };
    },
    [campaignIdMemo, fetchServiceValidName, t]
  );

  const handleSubmitData = useCallback(
    async (nameValue: string) => {
      setLoading(true);

      const duplicatedActionId = await duplicateAction({
        actionName: actionName?.startsWith('whatsapp-')
          ? `${WHATSAPP_PREFIX}${nameValue}`
          : nameValue,
        campaignActionId: actionIdMemo,
        actionType: actionTypeMemo as number,
      });

      if (duplicatedActionId) {
        const isRule = actionTypeMemo === EChannelsID.EMAIL_TRIGGER;

        if (isRule) {
          datadogRum.addAction('rules.duplicate', {
            ruleId: duplicatedActionId,
            newName: nameValue,
            creationTimestamp: Date.now(),
            isNewRule: true,
          });
        }

        const actionRedirect = window.location.pathname.replace(
          `/${Number(actionId)}`,
          `/${duplicatedActionId}`
        );
        actionDetailQuickTools?.origin === 'detail'
          ? navigate(actionRedirect, { replace: true })
          : await getActions({ ...filtersAction });
      }

      handleCloseModal();
      setLoading(false);
    },
    [
      setLoading,
      duplicateAction,
      actionName,
      actionIdMemo,
      actionTypeMemo,
      handleCloseModal,
      actionId,
      actionDetailQuickTools?.origin,
      navigate,
      getActions,
      filtersAction,
    ]
  );

  useEffect(() => {
    if (!toggleDuplicateActionsModal || duplicateActionInputValue || !actionName || loading) return;
    const inputValue = `${actionName.replace(WHATSAPP_PREFIX, '')} ${t(
      `${TRANSLATE_MODAL_DUPLICATE}.copy`
    )}`;
    setDuplicateActionInputValue(inputValue);
  }, [
    actionName,
    setDuplicateActionInputValue,
    t,
    duplicateActionInputValue,
    loading,
    toggleDuplicateActionsModal,
  ]);

  return {
    state: {
      inputValue: duplicateActionInputValue,
      alertConfig,
      disabledSubmitButton,
      VALIDATION_RULES,
      loading,
    },
    events: {
      handleValidation,
      handleCloseModal,
      handleSubmitData,
    },
  };
};
